import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent, Stack, TextField, Typography, useTheme
} from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router';
import { appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import { defaultMappingsId } from 'src/contexts/ImportContext';
import LicenseContext from 'src/contexts/LicenseContext';
import { PlaceSaveConditions } from 'src/entities/orderEntity';
import useImport from 'src/hooks/useImport';
import { saveAs } from 'src/utils/fileSaver';

import Footer from './Footer';
import Header from './Header';
import { hasImportRuleDetailSettings } from './importRuleDetailSettings';
import RuleFileList, { matchRuleFile } from './RuleFileList';
import RuleWarnings from './RuleWarnings';

const NewImport: FC = memo(() => {
  const importContext = useImport();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [uploaded, setUploaded] = useState<boolean>(false);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  // 警告の承認は「いま送ったもの」に対するもの。送る中身（ファイル・種別・ルール・地点の設定）が
  // 変わったら取り消して、送り直してもらう。地点の設定はアップロードのときに取込へ保存されるので、
  // 変えたまま進めると画面と違う設定で地点補完が走る
  const invalidateUpload = () => setUploaded(false);

  const selectedRule = useMemo(
    () => importContext.mappingRules.find((rule) => rule.id === importContext.selectedRuleId),
    [importContext.mappingRules, importContext.selectedRuleId]
  );
  // 取込ルール（import_rules/）を使う取込だけが複数ファイルを受け取れる（設計書 #405）
  const ruleKey = selectedRule?.ruleKey ?? null;
  const ruleFiles = useMemo(() => selectedRule?.ruleFiles ?? [], [selectedRule]);
  const ruleWarnings = useMemo(() => importContext.ruleWarnings ?? [], [importContext.ruleWarnings]);
  // 名前から種別が決まらないファイルは、画面で選んでもらうまで送らない。
  // Why not 送ってからエラーを出す: API は「どのファイルにも当てはまりません」で 400 を返すだけで、
  // 押す前に気づけない。RuleFileList が同じ照合でプルダウンを出している行がそのまま対象になる
  // Why not importContext.files をそのまま読む: ここは ruleKey の有無によらず毎回走るので、
  // 取込ルールを使わない画面のように files を持たない文脈では undefined が来る
  const hasUnassignedFile = useMemo(
    () => (importContext.files ?? []).some(
      (file) => !matchRuleFile(file.name, ruleFiles) && !(importContext.fileKinds ?? {})[file.name]
    ),
    [importContext.files, importContext.fileKinds, ruleFiles]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      invalidateUpload();
      if (!ruleKey) {
        importContext.setFile(acceptedFiles[0]);
        return;
      }

      // 何回かに分けて落とせるように足していく。同じ名前は落とし直したほうで置き換える
      // （古いほうを残すと、直したファイルを落としたつもりで直す前の中身が送られる）
      const kept = importContext.files.filter((file) => !acceptedFiles.some((dropped) => dropped.name === file.name));
      importContext.setFiles([...kept, ...acceptedFiles]);
    },
    [importContext, ruleKey]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    multiple: !!ruleKey,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    noClick: true,
    noKeyboard: true
  });

  const handleChange = (event: SelectChangeEvent) => {
    invalidateUpload();
    localStorage.setItem('importSelectedRuleId', event.target.value);
    importContext.setSelectedRuleId(Number(event.target.value));
    onChangeSkipMapping(null, false);
  };

  const onClickEditMappingRules = () => {
    void navigate('/mapping_rules');
  };

  const [placeSaveCondition, setPlaceSaveCondition] = useState<PlaceSaveConditions>('unloadingPlace');

  const onChangePlaceSaveCondition = (event: SelectChangeEvent) => {
    invalidateUpload();
    importContext.setPlaceSaveCondition(event.target.value);
    setPlaceSaveCondition(event.target.value as PlaceSaveConditions);
  };

  const [companyName, setCompanyName] = useState<string>('');
  useEffect(() => {
    if (!licenseContext.config) return;

    if (licenseContext.config.selectable_companies?.length > 1) {
      const selectedCompany = licenseContext.config.selectable_companies.find((company) => company.id === licenseContext.config.selected_company_id);
      setCompanyName(selectedCompany?.name);
    }
  }, [licenseContext.config]);

  const onChangeUsePlaceComplement = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    invalidateUpload();
    localStorage.setItem('usePlaceComplement', (checked).toString());
    importContext.setUsePlaceComplement(checked);
  };

  const onChangePrioritizePlaces = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    invalidateUpload();
    localStorage.setItem('prioritizePlaces', (checked).toString());
    importContext.setPrioritizePlaces(checked);
  };

  const onChangeSkipMapping = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    localStorage.setItem('importSkipMapping', checked.toString());
    importContext.setSkipMapping(checked);
  };

  const onChangeDuplicateDiffAction = (value: 'skip' | 'overwrite') => {
    importContext.setDuplicateDiffAction(value);
  };

  useEffect(() => {
    importContext.setWeightFactor(null);
    importContext.setVolumeFactor(null);
    importContext.setDuplicateDiffAction('overwrite');

    const rule = importContext.mappingRules.find((r) => r.id === importContext.selectedRuleId);
    if (!rule) {
      setIsExpanded(false);
      return;
    }

    setIsExpanded(hasImportRuleDetailSettings(rule));
    importContext.setWeightFactor(rule.weightFactor ?? null);
    importContext.setVolumeFactor(rule.volumeFactor ?? null);
    importContext.setDuplicateDiffAction(rule.duplicateDiffAction || 'overwrite');
  }, [importContext.mappingRules, importContext.selectedRuleId]);

  useEffect(() => {
    const importSelectedRuleId = localStorage.getItem('importSelectedRuleId');
    if (importSelectedRuleId && Number(importSelectedRuleId) !== importContext.selectedRuleId) {
      importContext.setSelectedRuleId(Number(importSelectedRuleId));
    }
    const usePlaceComplement = localStorage.getItem('usePlaceComplement') !== 'false';
    if (importContext.usePlaceComplement !== usePlaceComplement) {
      importContext.setUsePlaceComplement(usePlaceComplement);
    }
    const prioritizePlaces = localStorage.getItem('prioritizePlaces') === 'true';
    if (importContext.prioritizePlaces !== prioritizePlaces) {
      importContext.setPrioritizePlaces(prioritizePlaces);
    }
    const skipMapping = localStorage.getItem('importSkipMapping') === 'true';
    if (importContext.skipMapping !== skipMapping) {
      importContext.setSkipMapping(skipMapping);
    }
  }, [importContext]);

  // 変換ルールの一覧はマウント後に読むので、読み終える前に落としたファイルはルールを知らないまま
  // file に入る。そのままだと一覧にも出ず「次へ」も押せないので、ルールが分かった時点で移し替える
  useEffect(() => {
    if (!ruleKey || !importContext.file || importContext.files.length > 0) return;

    importContext.setFiles([importContext.file]);
  }, [ruleKey, importContext.file, importContext.files]);

  // 警告が出たときは「このまま取り込む」を押してもらってから進む（設計書 #405）
  useEffect(() => {
    if (!uploaded || ruleWarnings.length > 0) return;

    setUploaded(false);
    importContext.setStep(4);
  }, [uploaded, ruleWarnings]);

  // 親の main は高さが中身なりで height: 100% が効かないため、親の Box p={1} の上下余白を引いた高さを直接指定する
  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight}px - ${theme.spacing(2)})`, [theme]);

  return (
    <Box
      sx={{
        height: contentHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Header step={1} />
      </Box>
      <Box
        flexGrow={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          overflow: 'auto',
        }}
      >
        <Stack
          spacing={1}
          sx={{
            py: 2,
            width: 600
          }}
        >
          {companyName && (
            <Stack pb={2}>
              <Typography variant="body1">
                {companyName}
                のデータをインポートします。
              </Typography>
            </Stack>
          )}
          <FormControl
            fullWidth
          >
            <InputLabel>登録された変換ルールの呼び出し</InputLabel>
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}
            >
              <Select
                value={`${importContext.selectedRuleId}`}
                label="登録された変換ルールの呼び出し"
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value={0}>(新規)</MenuItem>
                <MenuItem value={defaultMappingsId}>Log標準フォーマット</MenuItem>
                {importContext.mappingRules.map((rule) => (
                  <MenuItem
                    key={['mappingsRile', rule.id].join('-')}
                    value={rule.id}
                    selected={rule.id === importContext.selectedRuleId}
                  >
                    {rule.name}
                  </MenuItem>
                ))}
              </Select>
              <Button variant="contained" sx={{ whiteSpace: 'nowrap' }} onClick={onClickEditMappingRules}>
                編集
              </Button>
            </Stack>
          </FormControl>
          <Box
            {...getRootProps()}
            sx={{
              width: '100%',
              color: 'rgba(110, 119, 129, 1)',
              border: '5px dashed #E2E4E6',
              borderRadius: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 6
            }}
          >
            <input {...getInputProps()} />
            <Box sx={{ my: '1em' }}>
              {importContext?.file ? (
                importContext.file.name
              ) : (
                // 外側の Box は flex item で margin が相殺されないため、元の p の入れ子と同じ余白を内側にも付ける
                <Box sx={{ my: '1em' }}>
                  <div>
                    ドラッグ＆ドロップするか、
                    <Button
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        open();
                      }}
                      sx={{
                        textDecoration: 'underline',
                        textTransform: 'none',
                        minWidth: 'auto',
                        padding: 0,
                        verticalAlign: 'baseline'
                      }}
                    >
                      ファイルを選択して
                    </Button>
                    アップロードします。
                  </div>
                  <div>ファイルタイプとしては.csv、.xlsx、.xlsがサポートされます。</div>
                </Box>
              )}
            </Box>
          </Box>
          {ruleKey && importContext.files.length > 0 && (
            <RuleFileList
              files={importContext.files}
              ruleFiles={ruleFiles}
              fileKinds={importContext.fileKinds}
              onChangeKind={(fileName, kind) => {
                invalidateUpload();
                importContext.setFileKind(fileName, kind);
              }}
              onRemove={(fileName) => {
                invalidateUpload();
                importContext.setFiles(importContext.files.filter((file) => file.name !== fileName));
              }}
            />
          )}
          {uploadErrors.length > 0 && (
            <Alert severity="error">
              <AlertTitle>取り込めませんでした</AlertTitle>
              <List dense disablePadding>
                {uploadErrors.map((message) => (
                  <ListItem key={message} disableGutters sx={{ display: 'list-item', listStyleType: 'disc', ml: 2 }}>
                    {message}
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          {/* 承認は今のファイルの取込に対するもの。送り直しや失敗のあとは出さない */}
          {uploaded && ruleWarnings.length > 0 && (
            <RuleWarnings
              warnings={ruleWarnings}
              onAccept={() => {
                setUploaded(false);
                importContext.setStep(4);
              }}
            />
          )}
          <Stack direction="column" spacing={0}>
            <FormControlLabel
              control={<Checkbox checked={importContext.usePlaceComplement} onChange={onChangeUsePlaceComplement} />}
              label="インポート時に地点マスタと紐づける"
            />
            <FormControlLabel
              control={<Checkbox disabled={!importContext.usePlaceComplement} checked={importContext.prioritizePlaces} onChange={onChangePrioritizePlaces} />}
              label="地点名称が一致する場合、地点情報を優先する"
              sx={{ ml: 2, }}
            />
          </Stack>
          <FormControl
            fullWidth
          >
            <RadioGroup
              row
              value={placeSaveCondition}
              onChange={onChangePlaceSaveCondition}
            >
              <FormControlLabel value="loadingPlace" control={<Radio />} label="地点の新規登録時、制約条件を積地に紐づけて保存する" />
              <FormControlLabel value="unloadingPlace" control={<Radio />} label="地点の新規登録時、制約条件を降地に紐づけて保存する" />
            </RadioGroup>
          </FormControl>
          {/* 取込ルールを使う取込は API が必ず上書きに倒す（ImportShipmentOrders::Runner#duplicate_diff_action）。
              選ばせると「スキップする」を選んでも上書きされ、画面と処理が食い違う */}
          {ruleKey ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 1,
                borderColor: '#E4E9F2',
                boxShadow: 'none',
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h6" fontWeight={700}>
                  重複データの処理
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  既存案件は差分更新されます
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  ※重複判定は「荷主名 + 伝票番号」で行われます。伝票番号は取込ルールが組み立てます
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  ※内容に変更がない場合は自動的に取り込みがスキップされます
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 1,
                borderColor: '#E4E9F2',
                boxShadow: 'none',
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="h6" fontWeight={700}>
                    重複データの処理方法
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    同一の荷主名と伝票番号のデータが既に登録されている場合の処理方法を選択してください。
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {[
                    {
                      value: 'overwrite' as const,
                      label: '上書きする',
                      recommended: true,
                      description: '既に登録されているデータがある場合、内容に差分があれば新しいデータで更新します。'
                    },
                    {
                      value: 'skip' as const,
                      label: 'スキップする',
                      recommended: false,
                      description: '既に登録されているデータがある場合、新しいデータは取り込みません。'
                    }
                  ].map((option) => {
                    const selected = importContext.duplicateDiffAction === option.value;

                    return (
                      <Box
                        key={option.value}
                        onClick={() => onChangeDuplicateDiffAction(option.value)}
                        sx={{
                          p: 2,
                          border: '2px solid',
                          borderColor: selected ? '#4094F7' : '#E4E9F2',
                          borderRadius: 1,
                          backgroundColor: selected ? '#EAF4FF' : '#FFFFFF',
                          cursor: 'pointer',
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Radio
                            checked={selected}
                            value={option.value}
                            onChange={() => onChangeDuplicateDiffAction(option.value)}
                            sx={{ p: 0.25, mt: 0.25 }}
                          />
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle1" fontWeight={700}>
                                {option.label}
                              </Typography>
                              {option.recommended && (
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 0.75,
                                    backgroundColor: '#4094F7',
                                    color: '#FFFFFF',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  推奨
                                </Box>
                              )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                              {option.description}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    ※重複判定は「荷主名 + 伝票番号」で行われます
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    ※同じ伝票番号のデータで、内容に変更がない場合は自動的に取り込みがスキップされます
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
          <Paper>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={handleExpandClick}
              py={1}
              px={2}
            >
              <Typography variant="subtitle1">
                詳細設定
              </Typography>
              <IconButton size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
            <Collapse in={isExpanded}>
              <Stack
                py={1}
                px={2}
                spacing={1}
              >
                <Stack>
                  <TextField
                    margin="dense"
                    type="number"
                    fullWidth
                    id="weight_factor"
                    label="重量係数"
                    placeholder="インポートした重量に掛ける係数"
                    value={importContext.weightFactor ?? ''}
                    onChange={(event) => importContext.setWeightFactor(event.target.value ? Number(event.target.value) : null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                <Stack>
                  <TextField
                    margin="dense"
                    type="number"
                    fullWidth
                    id="volume_factor"
                    label="体積係数"
                    placeholder="インポートした体積に掛ける係数"
                    value={importContext.volumeFactor ?? ''}
                    onChange={(event) => importContext.setVolumeFactor(event.target.value ? Number(event.target.value) : null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Stack>
            </Collapse>
          </Paper>
          {!ruleKey && (
            <Stack direction="column" spacing={0}>
              <FormControlLabel
                control={<Checkbox disabled={importContext.selectedRuleId === 0} checked={importContext.skipMapping} onChange={onChangeSkipMapping} />}
                label="対応付け設定画面をスキップする"
              />
            </Stack>
          )}
        </Stack>
      </Box>
      <Box>
        <Footer
          showFormatDownloadButton
          handleDownloadFormatButton={() => {
            const requestPath = '/api/v3/orders/formats';
            const responseType = 'arraybuffer';

            importContext.setIsLoading(true);
            axios.get(requestPath, { responseType })
              .then((response) => {
                const filename = 'LOG-order-dataformat.xlsx';
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                const blob = new Blob([response.data], { type: responseType });
                saveAs(blob, filename);
              }).catch((e) => {
                enqueueSnackbar(fiveZeroZeroErrorMessage);
                throw e;
              }).finally(() => { importContext.setIsLoading(false); });
          }}
          handleBackButton={() => {

            void navigate(-1);
          }}
          handleNextButton={() => {
            importContext.setIsLoading(true);
            invalidateUpload();
            setUploadErrors([]);

            importContext.postImports()
              .then(() => {
                // ルール取込は YAML が案件項目を作るので対応付けの画面を通らない。
                // 警告が出ていたら読んでもらうため、進むのは useEffect に任せる
                if (ruleKey) {
                  setUploaded(true);
                } else if (importContext.selectedRuleId !== 0 && importContext.skipMapping) {
                  importContext.setStep(4);
                } else {
                  importContext.setStep(2);
                }
              })
              .catch((e: AxiosError<{ message: string; errors?: string[] }>) => {
                // 全体エラーは複数まとめて返るので、1 行ずつ画面に出す（設計書 #405）。
                // Why not 投げ直す: 画面に出したものは処理済みで、投げ直すと握り手の無い rejection になる
                const errors = e.response?.data?.errors ?? [];
                setUploadErrors(errors);
                if (errors.length > 0) return;

                enqueueSnackbar(e.response?.data?.message || fiveZeroZeroErrorMessage, { variant: 'error' });
                throw e;
              })
              .finally(() => {
                importContext.setIsLoading(false);
              });
          }}
          disabled={ruleKey ? importContext.files.length === 0 || hasUnassignedFile : !importContext.file}
        />
      </Box>
    </Box>
  );
});

export default NewImport;
